<template>
  <div class="position-relative">

    <div v-if="loading" class="text-right p-4 position-absolute w-100" style="z-index: 1">
      <div class="spinner-border text-secondary" role="status">
      </div>
    </div>
    <div id="map" style="min-height: calc(100vh - 135px)"></div>

  </div>
</template>

<script>
import MapBoxGL from 'mapbox-gl'
import {getCenterPoint} from "../../utils/calcCenterPoint";
import treeImg from '../../assets/img/map-tree.png'
import * as turf from '@turf/turf'
import Area from "../../api/Area"
import {mapGetters} from "vuex";

export default {
  name: 'MapBoxFullComponent',

  data() {
    return {
      areas: null,
      loading: false,
      accessToken: 'pk.eyJ1IjoibWFnaW9kZXYiLCJhIjoiY2t3YzdjZ2ltMWZlczJ3cDJubXFheHl0bSJ9.B1spSbf4EfBOlDhYVmRZMQ',
      container: 'map', // container ID
      style: 'mapbox://styles/josedvd123/ckyubv4uz001v14lcg26hetvw', // style URL
      zoom: 7, // starting zoom
      map: {}
    }
  },
  computed: {
    ...mapGetters("user", ["getWeb2ActiveAccount"]),
  },

  mounted() {
    this.createMap()
  },

  methods: {
    async createMap() {
      const center = [27.1545801, 61.8449184];
      MapBoxGL.accessToken = this.accessToken;
      this.map = new MapBoxGL.Map({
        container: this.container,
        style: this.style,
        center: center,
        zoom: this.zoom,
        optimize: true
      });

      this.loading = true;
      this.addLayers()
    },
    addLayers() {
      this.map.on('load', () => {
        this.map.loadImage(treeImg, async (error, image) => {
          if (error) throw error;

          this.map.addImage('tree-icon', image);
          const centerPoints = [];

          const response = await Area.getAreasMap();
          this.areas = response.data;
          this.loading = false;

          for (let area of this.areas) {
            if (area.lat && area.lng) {
              const centerPoint = [Number(area.lng), Number(area.lat)];
              
              const centerPointFeature = turf.feature({
                  'type': 'Point',
                  'coordinates': centerPoint
                },
                {
                  areaId: area.id,
                  area: area.area_ha || 0,
                  name: area.name,
                  trees: area.qty,
                  centerPoint: JSON.stringify(centerPoint)
                }
              );
              centerPoints.push(centerPointFeature);
            }
          }

          let allPerimeterCoordinates = [];

          this.map.addSource('perimeter-source', {
            'type': 'geojson',
            'data': turf.featureCollection(allPerimeterCoordinates)
          });

          this.map.addLayer({
            'id': 'perimeter',
            'type': 'fill',
            'source': 'perimeter-source', 
            'layout': {},
            'minzoom': 11,
            'paint': {
              'fill-color': '#42b883', 
              'fill-opacity': 0.2
            }
          });

          this.map.addLayer({
            'id': 'outline',
            'type': 'line',
            'source': 'perimeter-source',
            'layout': {},
            'minzoom': 11,
            'paint': {
              'line-color': '#0a7045',
              'line-width': 3
            }
          });

          this.map.addSource('area-source', {
            'type': 'geojson',
            'data': turf.featureCollection(centerPoints)
          });

          this.map.addLayer({
            "id": "area-units",
            "type": "symbol",
            minzoom: 11,
            "source": "area-source",
            "layout": {
              "symbol-placement": "point",
              "text-font": ["Open Sans Bold"],
              "text-field": '{trees} trees\n{area} ha', 
              "text-size": 12,
            },
            'paint': {
              'text-color': '#0a7045'
            },
          });

          this.map.addLayer({
            'id': 'area',
            'type': 'symbol',
            'source': 'area-source',
            'maxzoom': 11,
            'layout': {
              'icon-allow-overlap': true,
              'icon-image': 'tree-icon',
              'icon-size': 0.3
            }
          });

          const popup = new MapBoxGL.Popup({
            closeButton: false,
            closeOnClick: false
          });

          let allLayers = ['perimeter', 'outline', "area-units", 'area']

          if (this.getWeb2ActiveAccount && this.getWeb2ActiveAccount.role.name === 'Administrator')
            this.map.on('click', allLayers, (e) => {
              const features = this.map.queryRenderedFeatures(e.point);
              const topFeature = features[0];
              if (topFeature?.properties?.areaId) {
                this.$router.push(`/areas/${topFeature.properties.areaId}`)
              }
            });

          this.map.on('mouseenter', allLayers, (e) => {
            this.map.getCanvas().style.cursor = 'pointer';
            const features = this.map.queryRenderedFeatures(e.point);
            for (const topFeature of features) {
              if (topFeature?.properties?.centerPoint) {
                popup.setLngLat(JSON.parse(topFeature.properties.centerPoint)).setHTML(`<span style="color: #4b6b41; font-weight: bold">${topFeature.properties.name}</span>`).addTo(this.map);
              }
            }
          });

          this.map.on('mouseleave', allLayers, () => {
            this.map.getCanvas().style.cursor = '';
            popup.remove();
          });
          
          let loadedPerimeters = new Set();
          
          this.map.on('idle', async () => {
             const zoom = this.map.getZoom();
             if (zoom < 10.5) return;
             
             const features = this.map.queryRenderedFeatures({ layers: ['area-units', 'area'] });
             const visibleIds = features.map(f => f.properties.areaId).filter(id => id && !loadedPerimeters.has(id));
             
             const idsToLoad = [...new Set(visibleIds)].slice(0, 60); 
             if (idsToLoad.length === 0) return;
             
             idsToLoad.forEach(id => loadedPerimeters.add(id));
             
             try {
                const perimRes = await Area.getAreasPerimeters(idsToLoad);
                 for (const area of perimRes.data) {
                    let coords = area.perimeterCoordinates;
                    if (typeof coords === 'string') {
                      try { coords = JSON.parse(coords); } catch(err) { coords = []; }
                    }
                    if (Array.isArray(coords) && coords.length > 3) {
                       const perimeterCoordinatesFeature = turf.polygon([coords], {
                         areaId: area.id,
                         name: this.areas.find(a => a.id === area.id)?.name || '',
                         centerPoint: JSON.stringify(getCenterPoint(coords))
                       });
                       allPerimeterCoordinates.push(perimeterCoordinatesFeature);
                    }
                 }
                this.map.getSource('perimeter-source').setData(turf.featureCollection(allPerimeterCoordinates));
             } catch(e) {
                console.error("Failed lazy loading perimeters:", e);
             }
          });

        });
      });
    }
  }
}
</script>

<style scoped>
.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #3A5333;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spinner-animation 1s linear infinite;
}

@keyframes spinner-animation {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

</style>
